import { isArray, isEmpty, isPlainObject } from 'lodash';
import DOMPurify from 'dompurify';
import { enqueueSnackbar } from 'notistack';
import { configKeys, cssKeys, cssVariables } from 'src/constants/chatbotConstants';

export const getCustomCss = (customCss) => {
    let cssFields = '';

    cssVariables.forEach(([key, cssVar]) => {
        if (customCss[key]) {
            cssFields += `\r\n--${cssVar}: ${customCss[key]};`;
        }
    });

    return cssFields ? `:root {${cssFields}\r\n}` : '';
};

export const getCSSValues = (cssString, keys) => {
    const values = {};

    keys.forEach(key => {
        const pattern = new RegExp(`--${key}:\\s*([^;]+);`);
        const match = cssString.match(pattern);
        if (match) {
            values[key] = match[1].trim();
        } else {
            values[key] = null;
        }
    });
    return values;
};

const getCleanedHtmlForImg = (value) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, 'text/html');

    const images = doc.querySelectorAll('img');

    if (images) {
        images?.forEach((img) => {
            img.removeAttribute('style');
            img.removeAttribute('data-proc');
        });

        const cleanedHTML = doc.body.innerHTML;

        return cleanedHTML;
    }
    return value;
};

// ── Welcome messages (platform messages) ─────────────────────────────────────────
// Legacy saves wrapped every message in an extra <div> (once per save, so repeated saves nested them)
// and left runs of empty paragraphs. Normalise on load; never re-wrap on save.
const parseBody = (html) => new DOMParser().parseFromString(String(html || ''), 'text/html').body;

export const normaliseLegacyMessageHtml = (html) => {
    if (!html || !String(html).trim()) return '';
    const body = parseBody(String(html).trim());
    // unwrap plain outer <div>s (no attributes) that are the ONLY child of the message
    let guard = 0;
    while (
        guard < 20
        && body.childNodes.length === 1
        && body.firstElementChild
        && body.firstElementChild.tagName === 'DIV'
        && body.firstElementChild.attributes.length === 0
    ) {
        body.innerHTML = body.firstElementChild.innerHTML;
        guard += 1;
    }
    return body.innerHTML.replace(/(?:<p><br\s*\/?><\/p>\s*){2,}/gi, '<p><br></p>');
};

const KEEP_IMG_STYLE = new Set(['width', 'max-width']);

// Images keep width / max-width only (the old code stripped style wholesale, which made uploaded
// images overflow the bubble); everything else in the style and data-proc is dropped.
export const cleanWelcomeMessageHtml = (html) => {
    if (!html) return '';
    const body = parseBody(html);
    body.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('data-proc');
        const kept = String(img.getAttribute('style') || '')
            .split(';')
            .map((decl) => decl.split(/:(.+)/).map((x) => (x || '').trim()))
            .filter(([prop, value]) => KEEP_IMG_STYLE.has((prop || '').toLowerCase()) && value)
            .map(([prop, value]) => `${prop.toLowerCase()}: ${value.replace(/\s*!important/i, '')}`);
        img.setAttribute('style', kept.length ? kept.join('; ') : 'max-width: 100%');
    });
    return body.innerHTML;
};

export const isBlankMessageHtml = (html) => {
    if (!html) return true;
    if (/<(img|iframe|video)\b/i.test(html)) return false;
    const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
    return text.length === 0;
};

export const buildWelcomeMessagesPayload = (messages) => {
    if (!isArray(messages)) return [];
    return messages
        .filter((ele) => !isBlankMessageHtml(ele?.message))
        .map((ele, index) => ({
            ...(ele.serverId ? { id: ele.serverId } : {}),
            message: cleanWelcomeMessageHtml(ele.message),
            is_active: ele.active || false,
            order: index,
        }));
};

// Loads the UNRENDERED raw_message (merge tags intact) — `message` is the rendered copy with tags baked in.
export const welcomeMessagesFromPlatform = (messages) => {
    if (!isArray(messages)) return [];
    return messages
        .map((m, index) => ({
            serverId: m.id,
            message: normaliseLegacyMessageHtml(m.raw_message ?? m.message ?? ''),
            active: m.is_active || false,
            order: m.order,
            _index: index,
        }))
        .sort((a, b) => ((a.order ?? a._index) - (b.order ?? b._index)) || (a._index - b._index))
        .map((m) => ({ serverId: m.serverId, message: m.message, active: m.active, order: m.order }));
};

const tagLabel = (tag) => {
    const text = String(tag).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};

// Sanitised preview HTML for the chat bubble: known merge tags (`$practice_name`) become [Practice name] chips.
export const renderWelcomePreviewHtml = (html, knownTags = []) => {
    const clean = DOMPurify.sanitize(String(html || ''), { ADD_TAGS: ['iframe'], ADD_ATTR: ['allowfullscreen', 'frameborder'] });
    const known = new Set(knownTags);
    const body = parseBody(clean);
    // embedded videos (Quill video blot) are allowed only from the usual players
    body.querySelectorAll('iframe').forEach((frame) => {
        if (!/^https:\/\/(player\.vimeo\.com|www\.youtube(-nocookie)?\.com)\//i.test(frame.getAttribute('src') || '')) frame.remove();
    });
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach((node) => {
        const text = node.nodeValue;
        if (!text.includes('$')) return;
        const parts = text.split(/(\$[A-Za-z_][A-Za-z0-9_]*)/g);
        if (parts.length === 1) return;
        const frag = document.createDocumentFragment();
        parts.forEach((part) => {
            if (part.startsWith('$') && known.has(part.slice(1))) {
                const chip = document.createElement('span');
                chip.setAttribute('data-merge-tag', part.slice(1));
                chip.setAttribute('style', 'background:#e0e7ff;color:#3730a3;border-radius:4px;padding:0 4px;font-weight:600;white-space:nowrap');
                chip.textContent = `[${tagLabel(part.slice(1))}]`;
                frag.appendChild(chip);
            } else if (part) {
                frag.appendChild(document.createTextNode(part));
            }
        });
        node.parentNode.replaceChild(frag, node);
    });
    return body.innerHTML;
};

// WhatsApp click-to-chat links must be https:// (wa.me / api.whatsapp.com / whatsapp.com recommended).
// Returns an error message, or '' when the value is empty or acceptable.
export const getWhatsappUrlError = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    let url;
    try {
        url = new URL(text);
    } catch (e) {
        return 'Enter a full https:// link, e.g. https://wa.me/447700900123';
    }
    if (url.protocol !== 'https:') {
        return 'WhatsApp URL must start with https:// (e.g. https://wa.me/447700900123)';
    }
    return '';
};

export const handleSubmitChatbot = (data, handleSuccess, pathname) => {
    const payload = {
        body: {
            name: data.chatbotName,
            is_active: data.active,
            customisations: data.customisations
        },
        handleSuccess
    };
    if (isArray(data.chatbot)) {
        payload.body.chatbot_platforms = data.chatbot.map((field) => {
            const chatBotPlatformSettings = field.chatbot_platform_settings;
            const { icon, custom_css_file: customCssFile, custom_css: customCss, config, originalConfig } = chatBotPlatformSettings;
            // Start from the config as loaded so keys this editor doesn't manage (e.g. the
            // website-chat options set in the super-admin drawer) survive a save verbatim.
            const preservedConfig = isPlainObject(originalConfig) ? { ...originalConfig } : {};
            [...configKeys, 'treatmentIntents'].forEach((key) => delete preservedConfig[key]);
            const chatbotBody = {
                is_enabled: field.enabled,
                settings: {
                    config: {
                        ...preservedConfig,
                        autoOpen: config.autoOpen
                    }
                }
            };
            if (field.platform) {
                chatbotBody.platform = field.platform;
            }
            if (pathname.includes('edit-chatbot')) {
                chatbotBody.id = field.id;
            }
            configKeys.filter((ele) => ele !== 'autoOpen').forEach(key => {
                if (config[key]) {
                    chatbotBody.settings.config[key] = config[key];
                }
            });
            // Only the first treatmentIntents entry is editable in this form: replace just that entry
            // and keep the rest from the config as loaded (the form used to truncate to one entry).
            const formIntent = config.treatmentIntents || {};
            const originalIntents = isArray(originalConfig?.treatmentIntents) ? originalConfig.treatmentIntents : [];
            if (formIntent.event || formIntent.intent) {
                const first = formIntent.event
                    ? { event: formIntent.event, intent: formIntent.intent }
                    : { intent: formIntent.intent };
                chatbotBody.settings.config.treatmentIntents = [first, ...originalIntents.slice(1)];
            } else if (originalIntents.length > 0) {
                const firstOriginal = originalIntents[0]?.[0] || originalIntents[0];
                const hadEditableFirst = !!(firstOriginal?.event || firstOriginal?.intent);
                // cleared first entry -> drop it; an unreadable first entry is left untouched
                const kept = hadEditableFirst ? originalIntents.slice(1) : originalIntents;
                if (kept.length > 0) chatbotBody.settings.config.treatmentIntents = kept;
            }
            if (getCustomCss(customCss)) {
                chatbotBody.settings.custom_css = getCustomCss(customCss);
            }

            if (icon) {
                chatbotBody.settings.icon = icon;
            }
            if (customCssFile) {
                chatbotBody.settings.custom_css_file = customCssFile;
            }
            if (isArray(field.platform_message)) {
                // ids + order let the backend update/reorder in place (no extra <div> wrapper any more)
                chatbotBody.messages = buildWelcomeMessagesPayload(field.platform_message);
            }
            if (isArray(field.platform_treatments)) {
                chatbotBody.treatments = field.platform_treatments?.map((ele, treatmentIndex) => {
                    // Existing rows send their ids back so the API updates them in place (page-message rules point at
                    // the platform-treatment id). A changed treatment item is a different row: drop all its ids.
                    const keepIds = !ele.originalTreatmentId || ele.originalTreatmentId === ele.treatmentId;
                    const withServerId = (row) => (keepIds && row?.serverId ? { id: row.serverId } : {});
                    const treatmentsBody = {
                        ...withServerId(ele),
                        treatment: ele.treatmentId,
                        is_enabled: ele.enabled || false,
                        display_name: ele.display_name,
                        order: treatmentIndex
                    };
                    if (isArray(ele.offers)) {
                        treatmentsBody.offers = [];
                        ele.offers?.forEach((offer) => {
                            if (isEmpty(offer.offer)) return;
                            treatmentsBody.offers.push({ ...withServerId(offer), offer: `<div>${getCleanedHtmlForImg(offer.offer)}</div>` });
                        });
                    }
                    if (isArray(ele.messages)) {
                        treatmentsBody.treatment_messages = [];
                        ele.messages?.forEach((message) => {
                            if (isEmpty(message.message)) return;
                            treatmentsBody.treatment_messages.push({
                                ...withServerId(message),
                                message: `<div>${getCleanedHtmlForImg(message.message)}</div>`,
                                is_active: message.active || false
                            });
                        });
                    }
                    if (isArray(ele.urls)) {
                        treatmentsBody.treatment_urls = ele.urls?.map((url) => ({
                            ...withServerId(url),
                            url: url.url,
                            is_active: url.active || false
                        })) || [];
                    }
                    return treatmentsBody;
                }) || [];
            }
            return chatbotBody;
        }) || [];
    }
    return payload;
};
const handleSetTreatmentFields = ({ setValue, treatment, treatmentField, treatmentIndex }) => {
    const treatmentFields = {
        display_name: treatment.display_name,
        treatment: treatment.treatment.name,
        treatmentId: treatment.treatment.id,
        enabled: treatment.is_enabled,
        serverId: treatment.id,
        originalTreatmentId: treatment.treatment.id,
    };
    Object.entries(treatmentFields).forEach(([key, value]) => {
        setValue(`${treatmentField}.${treatmentIndex}.${key}`, value);
    });
};
export const handleChatbotEditData = (individualChatBotGlobal, setValue, append) => {
    setValue('chatbotName', individualChatBotGlobal.name);
    setValue('active', individualChatBotGlobal.is_active);
    if (individualChatBotGlobal.customisations) {
        const customizationFields = ['is_treatment_messages_enabled', 'is_lead_dob_enabled', 'is_lead_postcode_enabled', 'is_existing_patient'];
        customizationFields.forEach((ele) => {
            setValue(`customisations.${ele}`, individualChatBotGlobal.customisations?.[ele]);
        });
    }
    if (isArray(individualChatBotGlobal.chatbot_platforms) && individualChatBotGlobal.chatbot_platforms.length > 0) {
        individualChatBotGlobal.chatbot_platforms.forEach((platform, platformIndex) => {
            const dataToSet = {
                platform: platform.platform || '', enabled: platform.is_enabled || false, id: platform.id
            };
            if (!isEmpty(platform.settings)) {
                dataToSet.chatbot_platform_settings = {};
                dataToSet.chatbot_platform_settings.iconFile = platform.settings.icon || '';
                dataToSet.chatbot_platform_settings.customCssFile = platform.settings.custom_css_file || '';
                append(dataToSet);
                if (!isEmpty(platform.settings.config)) {
                    const configName = `chatbot.${platformIndex}.chatbot_platform_settings.config`;
                    const { config } = platform.settings;
                    // Keep the full original config (non-rendered) so save can preserve unmanaged keys.
                    setValue(`chatbot.${platformIndex}.chatbot_platform_settings.originalConfig`, config);
                    configKeys.forEach(key => {
                        setValue(`${configName}.${key}`, config[key] || (key === 'disabledPages' ? [] : config[key]));
                    });
                    if (isArray(config.treatmentIntents) && config.treatmentIntents.length > 0) {
                        setValue(`${configName}.treatmentIntents.event`, config.treatmentIntents[0]?.[0]?.event || config.treatmentIntents[0]?.event);
                        setValue(`${configName}.treatmentIntents.intent`, config.treatmentIntents[0]?.[0]?.intent || config.treatmentIntents[0]?.intent);
                    }
                }
                if (!isEmpty(platform.settings.custom_css)) {
                    const customCssName = `chatbot.${platformIndex}.chatbot_platform_settings.custom_css`;
                    const { custom_css: customCss } = platform.settings;
                    const cssValues = getCSSValues(customCss, cssKeys);
                    cssVariables.forEach(([cssKey, customCssKey]) => {
                        if (!isEmpty(cssValues[customCssKey])) {
                            setValue(`${customCssName}.${cssKey}`, cssValues[customCssKey]);
                        }
                    });
                }
            }
            if (isArray(platform.messages) && platform.messages.length > 0) {
                welcomeMessagesFromPlatform(platform.messages).forEach((message, messageIndex) => {
                    const platFormMessageKey = `chatbot.${platformIndex}.platform_message.${messageIndex}`;
                    setValue(`${platFormMessageKey}.message`, message.message);
                    setValue(`${platFormMessageKey}.active`, message.active);
                    setValue(`${platFormMessageKey}.serverId`, message.serverId);
                });
            }
            if (isArray(platform.treatments) && platform.treatments.length > 0) {
                platform.treatments.forEach((treatment, treatmentIndex) => {
                    const treatmentField = `chatbot.${platformIndex}.platform_treatments`;
                    handleSetTreatmentFields({ setValue, treatment, treatmentField, treatmentIndex });
                    if (isArray(treatment.treatment_messages) && treatment.treatment_messages.length > 0) {
                        treatment.treatment_messages.forEach((treatmentMessages, treatmentMessageIndex) => {
                            setValue(`${treatmentField}.${treatmentIndex}.messages.${treatmentMessageIndex}.message`, treatmentMessages.message);
                            setValue(`${treatmentField}.${treatmentIndex}.messages.${treatmentMessageIndex}.serverId`, treatmentMessages.id);
                            setValue(`${treatmentField}.${treatmentIndex}.messages.${treatmentMessageIndex}.active`, treatmentMessages.is_active);
                        });
                    }
                    if (isArray(treatment.offers) && treatment.offers.length > 0) {
                        treatment.offers.forEach((offer, offerIndex) => {
                            setValue(`${treatmentField}.${treatmentIndex}.offers.${offerIndex}.offer`, offer.offer);
                            setValue(`${treatmentField}.${treatmentIndex}.offers.${offerIndex}.serverId`, offer.id);
                        });
                    }
                    if (isArray(treatment.treatment_urls) && treatment.treatment_urls.length > 0) {
                        treatment.treatment_urls.forEach((url, urlIndex) => {
                            setValue(`${treatmentField}.${treatmentIndex}.urls.${urlIndex}.url`, url.url);
                            setValue(`${treatmentField}.${treatmentIndex}.urls.${urlIndex}.serverId`, url.id);
                            setValue(`${treatmentField}.${treatmentIndex}.urls.${urlIndex}.active`, url.is_active);
                        });
                    }
                });
            }
        });
    }
};

export const formErrorHandler = (errors) => {
    const errorMessages = [];

    if (errors.chatbotName) {
        errorMessages.push('Chatbot name');
    }
    const chatbotErrors = errors.chatbot?.find((ele) => ele)?.platform_treatments?.find((ele) => ele);

    if (chatbotErrors) {
        if (!isEmpty(chatbotErrors.treatment)) {
            errorMessages.push('Treatment');
        }
        if (!isEmpty(chatbotErrors.urls)) {
            errorMessages.push('Treatment url');
        }
    }

    if (errorMessages.length > 0) {
        const errorMessage = `${errorMessages.join(', ')} is required field`;
        enqueueSnackbar(errorMessage, {
            variant: 'error',
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
            },
        });
    }
};